//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { IScope } from 'angular';
import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { getIsFeatureHashtagTrackingAndInsightsEnabled } from '@account-settings/redux/facebook-instagram/selectors/getIsFeatureHashtagTrackingAndInsightsEnabled';
import Icon from '@components/atoms/icon/Icon';
import { RequiredPermissionsActionButton } from '@components/atoms/required-permissions-button/RequiredPermissionsButton';
import SidebarHeaderItem from '@components/atoms/sidebar-items/SidebarHeaderItem';
import { SidebarPageItemGroup } from '@components/atoms/sidebar-items/SidebarItemGroup';
import SidebarPageItem from '@components/atoms/sidebar-items/SidebarPageItem';
import Sidebar from '@components/organisms/sidebar/Sidebar';
import { getHasHarmonyAiCreatorsIntegrationFeatureEnabled } from '@influencers/selectors/getHarmonyAi';
import { getIsInfluencersDiscoveryEnabled } from '@influencers/selectors/getInfluencersLists';
import { getTikTokMentionsFeatureEnabled } from '@publishing/analytics/selectors/getTikTokMentionsFeaturesEnabled';
import makeAngularReactControllerWithScope, {
  applyScope,
} from '@react-bridge/angular-react-widget-with-scope';
import { clearSelectedItems } from '@streams-drawer/actions/streams-drawer-actions';
import { alphabetizeLocaleCompare } from '@utils/arrayUtils';

import { ICollectionSidebarState } from 'app/components/content/content-collections-sidebar';
import { LibraryTrackingIds } from 'app/components/content/libraryConstants';
import { getIsMultiHandleHashtagTrackingEnabled } from 'app/hashtags/selectors/getIsMultiHandleHashtagTrackingEnabled';

interface MediaCollectionsSidebarProps {
  collectionStates: ICollectionSidebarState[];
  createNewEmptyCollection: () => void;
}

export const MediaCollectionsSidebar = ({
  collectionStates,
  createNewEmptyCollection,
}: MediaCollectionsSidebarProps) => {
  const areSocialDashboardsEnabled = useSelector(
    (state) => state.session.features.social_dashboards,
  );

  const isHashtagTrackingAndInsightsEnabled = useSelector(
    getIsFeatureHashtagTrackingAndInsightsEnabled,
  );
  const isHarmonyAiCreatorsIntegrationEnabled = useSelector(
    getHasHarmonyAiCreatorsIntegrationFeatureEnabled,
  );
  const isInfluencersDiscoveryEnabled = useSelector(
    getIsInfluencersDiscoveryEnabled,
  );

  // TODO: PD-271441: Remove once feature is released
  const isMultiHandleHashtagTrackingEnabled = useSelector(
    getIsMultiHandleHashtagTrackingEnabled,
  );
  // TODO: PD-285704: Remove this once tiktok mentions ingestion feature flag is released
  const isTikTokMentionsEnabled = useSelector(getTikTokMentionsFeatureEnabled);

  const isTrackedUsersVisible =
    isHarmonyAiCreatorsIntegrationEnabled || isInfluencersDiscoveryEnabled;

  return (
    <Sidebar>
      <StyledPageItem>
        <SidebarPageItem
          state='library.all-media'
          text='All Media'
          trackingId={LibraryTrackingIds.ALL_MEDIA}
        />
      </StyledPageItem>

      <SidebarHeaderItem text={'Uploaded'} />

      <SidebarPageItem
        state='library.team-media'
        text={'Team Uploads'}
        trackingId={LibraryTrackingIds.TEAM_MEDIA}
      />

      <SidebarPageItem
        state='library.my-media'
        text={'My Uploads'}
        trackingId={LibraryTrackingIds.MY_MEDIA}
      />

      {areSocialDashboardsEnabled && (
        <>
          <SidebarHeaderItem text={'UGC'} />

          <SidebarPageItem
            linkBadge={<Icon name='linkout' />}
            state='instagramAccount.mentionedAndTagged'
            text='IG Mentioned &amp; Tagged'
            trackingId={LibraryTrackingIds.SIDEBAR_MENTIONED_AND_TAGGED}
          />
          {isTikTokMentionsEnabled && (
            <SidebarPageItem
              linkBadge={<Icon name='linkout' />}
              state='tiktok.mentions'
              text='TikTok Mentions'
              trackingId={LibraryTrackingIds.SIDEBAR_TIKTOK_MENTIONS}
            />
          )}

          {isTrackedUsersVisible && (
            <SidebarPageItem
              state='library.tracked-sources.user'
              text='Tracked Users'
              trackingId={LibraryTrackingIds.SIDEBAR_TRACKED_USERS}
            />
          )}

          {isHashtagTrackingAndInsightsEnabled &&
            (isMultiHandleHashtagTrackingEnabled ? (
              <SidebarPageItem
                state='library.hashtags'
                text='Hashtags'
                trackingId={LibraryTrackingIds.SIDEBAR_HASHTAGS}
              />
            ) : (
              <SidebarPageItem
                state='library.tracked-sources.hashtag'
                text='Hashtags'
                trackingId={LibraryTrackingIds.SIDEBAR_TRACKED_HASHTAGS}
              />
            ))}
        </>
      )}

      <SidebarPageItemGroup
        containerItem={
          <SidebarHeaderItem
            headerAction={
              <RequiredPermissionsActionButton
                iconName='add'
                label='Create New Collection'
                onClick={createNewEmptyCollection}
                permissionName='library:manage_collections'
                trackingId={LibraryTrackingIds.SIDEBAR_COLLECTION}
              />
            }
            isExpandable={true}
            text='Collections'
          />
        }
        initExpanded
        nestedItems={alphabetizeLocaleCompare(
          collectionStates,
          (collection) => collection.name,
        ).map((collection) => (
          <SidebarPageItem
            key={collection.name}
            onClick={clearSelectedItems}
            state={collection.state.view}
            stateParams={collection.state.params}
            text={collection.name}
          />
        ))}
        useNestedItemStyles={false}
      />
    </Sidebar>
  );
};

const StyledPageItem = styled.div(
  ({ theme }) => `
    padding-top: ${theme.spacings[3]};
  `,
);

angular.module('curalate').component('mediaCollectionsSidebar', {
  bindings: {
    collectionStates: '<',
    createNewEmptyCollection: '&',
  },
  controller: makeAngularReactControllerWithScope(
    (scope: { $ctrl: MediaCollectionsSidebarProps } & IScope) => {
      return (
        <MediaCollectionsSidebar
          collectionStates={scope.$ctrl.collectionStates}
          createNewEmptyCollection={applyScope(
            scope,
            scope.$ctrl.createNewEmptyCollection,
          )}
        />
      );
    },
  ),
});

export default MediaCollectionsSidebar;
